// The document-type matrix lives in the shared single source of truth (`@/shared/data/document-types`) so the
// clients, dispersions, cards and onboarding KYC flows never drift. This module re-exports it and adds the
// clients-namespace i18n label key.

import type { DocumentTypeCode, FixedLengthDocumentCode } from "@/shared/data/document-types";

export { documentTypesFor, isDocumentNumberLengthValid } from "@/shared/data/document-types";
export type { DocumentTypeCategory } from "@/shared/data/document-types";

/**
 * I18n key (clients namespace) for a document-type code, e.g. "CC" -> "doc_type_cc".
 *
 * Generic over the code, so the key is the exact literal and adding a document type without its three
 * translations is a compile error at the call site rather than a raw `doc_type_x` rendered on screen. The
 * parameter is a `DocumentTypeCode` and not a `string`: a code read off an API response would otherwise
 * type-check its way into a label that does not exist.
 */
export const documentTypeLabelKey = <C extends DocumentTypeCode>(code: C) =>
  `doc_type_${code.toLowerCase() as Lowercase<C>}` as const;

/**
 * Error message key (clients namespace) for each document type that has a fixed length.
 *
 * Keyed by `FixedLengthDocumentCode`, so a new rule in `DOCUMENT_LENGTH_RULES` without a message here fails
 * to compile. That matters because the call sites read `if (message) addIssue(...)`: a missing key would not
 * throw, it would silently skip the check and leave a rule that looks enforced and is not.
 */
export const DOCUMENT_LENGTH_MESSAGE_KEYS: Record<FixedLengthDocumentCode, DocumentLengthMessageKey> = {
  CPF: "validation_cpf_digits",
  CNPJ: "validation_cnpj_length",
};

export type DocumentLengthMessageKey = "validation_cpf_digits" | "validation_cnpj_length";

export const documentLengthMessageKey = (code: string): DocumentLengthMessageKey | undefined =>
  DOCUMENT_LENGTH_MESSAGE_KEYS[code as FixedLengthDocumentCode];
